import { getInitials } from "@upsquad/shared";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { AuthLayout } from "@/src/auth/components/auth-layout";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Avatar, Button, Label, Radio, RadioGroup } from "@/src/common/components/ui";
import { PRIVACY_URL, TOS_URL } from "@/src/common/constants/urls";
import { formatDisplayDate } from "@/src/common/lib/date.utils";
import { useAppSelector } from "@/src/common/redux/store";
import { useCommunityNavigation } from "@/src/community/hooks/use-community-navigation";
import { getCommunities } from "@/src/community/store/community-selectors";

export default function SelectCommunityPage() {
	const { t } = useTranslation("onboarding");
	const navigate = useNavigate();
	const communities = useAppSelector(getCommunities);
	const [selectedSlug, setSelectedSlug] = useState<string>(communities[0]?.slug ?? "");
	const { goToCommunity } = useCommunityNavigation();
	const [isProceeding, setIsProceeding] = useState(false);

	const handleProceed = async () => {
		if (!selectedSlug || isProceeding) return;
		setIsProceeding(true);
		await goToCommunity(selectedSlug);
	};

	return (
		<>
			<PageHelmet title="Select Community" />
			<AuthLayout>
				<div className="w-full max-w-md">
					<button
						type="button"
						onClick={() => navigate(-1)}
						className="flex items-center gap-1 text-sm text-foreground/50 hover:text-foreground mb-6"
					>
						<ChevronLeft className="w-4 h-4" />
						{t("back")}
					</button>

					<h1 className="text-2xl font-bold mb-8">{t("select_title")}</h1>

					<RadioGroup value={selectedSlug} onChange={(value) => setSelectedSlug(value)}>
						{communities.map((community) => (
							<Radio key={community.id} value={community.slug}>
								<Radio.Control>
									<Radio.Indicator />
								</Radio.Control>
								<Radio.Content>
									<div className="flex items-center gap-3">
										<Avatar size="sm">
											<Avatar.Image src={community?.image ?? undefined} alt={community.name} />
											<Avatar.Fallback>{getInitials(community.name)}</Avatar.Fallback>
										</Avatar>
										<div>
											<Label className="font-medium text-sm">{community.name}</Label>
											<p className="text-xs text-foreground/40">{t("select_joined", { date: formatDisplayDate(community.created_at) })}</p>
										</div>
									</div>
								</Radio.Content>
							</Radio>
						))}
					</RadioGroup>

					<Button variant="primary" fullWidth className="mt-6" onPress={handleProceed} isDisabled={!selectedSlug} isPending={isProceeding}>
						{t("select_proceed")}
					</Button>

					<p className="text-xs text-foreground/40 text-center mt-4">
						{t("select_terms")}{" "}
						<a href={TOS_URL} className="text-accent underline">
							{t("select_tos")}
						</a>
						{". "}
						{t("select_read_our")}{" "}
						<a href={PRIVACY_URL} className="text-accent underline">
							{t("select_privacy")}
						</a>
					</p>
				</div>
			</AuthLayout>
		</>
	);
}
